import { dehydrate, HydrationBoundary, QueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { v1Api } from '@/api/backendApi';
import type { CurrentPriceResponse } from '@/hooks/api/useCurrentPrice';
import type { PricingOption } from '@/hooks/api/usePricingOptions';
import { isBillingMode, useHydrateBillingMode } from '@/atoms/billing';
import FAQs from '@/components/FAQs';
import { useOpenPricingSignupDialog } from '@/components/DialogStage/pricingSignupFlow';
import { UBLogo } from '@/components/Logo';
import UpgradePlanModal from '@/components/Modals/UpgradePlan/modal';
import PaymentAssurance from '@/components/PaymentAssurance';
import { ApiBanner } from '@/components/PricingPage/ApiBanner';
import { CustomLicensingBanner } from '@/components/PricingPage/CustomLicensingBanner';
import { FreePlanBanner } from '@/components/PricingPage/FreePlanBanner';
import { PricingCards } from '@/components/PricingPage/PricingCards';
import { PricingTable } from '@/components/PricingPage/PricingTable';
import { ReviewsSection } from '@/components/PricingPage/ReviewsSection';
import { SafeForAllPlatformsSection } from '@/components/PricingPage/SafeForAllPlatformsSection';
import { SectionTitle } from '@/components/PricingPage/SectionTitle';
import { PRICING_FAQS, pricingTableFaqFAQItem } from '@/components/PricingPage/types';
import { headerTitle } from '@/components/PricingPage/utils';
import { SEO } from '@/components/SEO';
import { Alpha } from '@music-vine/cadence/ui';
import { Text } from '@music-vine/cadence/ui';
import { pricingQuery, usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useIsLoggedIn } from '@/hooks/auth';
import { VALID_PAID_PLAN_NAMES } from '@/pages/subscribe/types';
import { resolveCurrencyFromRequest } from '@/lib/currency/resolveCurrency';
import { normalizeIsoCountry } from '@/lib/geo/country';
import { withGrowthBook } from '@/lib/growthbook';
import { withServerAuth } from '@/lib/auth/withServerAuth';
import { PAID_PLAN_ROLES } from '@/lib/pricing/paidSubscriberRoles';
import { useTaxExclusiveNote } from '@/lib/pricing/useTaxExclusiveNote';
import { applyPricingOverridesToApiResponse, PricingCohortProvider, resolveCohortSSR } from '@/lib/pricing-test';
import { systemKeys } from '@/utils/queryKeys';
import { upgradePlanModalPlanAtom } from './atoms';
import type { UserDataCookie } from '@/hooks/state/buildUserDataCookie';
import type { Settings } from '@/types/settings';
import type { DehydratedState } from '@tanstack/react-query';
import type { NextPage } from 'next';
import Footer from '@/components/Footer';
import { CustomPackageBanner } from '@/components/PricingPage/CustomPackageBanner';
interface PricingPageProps {
  country: string;
  currency: Settings['currency'];
  dehydratedQuery: DehydratedState;
}

// Must render inside HydrationBoundary + PricingCohortProvider so SSR reads the prefetched prices.
const PricingFaqs = ({
  currency
}: {
  currency: Settings['currency'];
}) => {
  const {
    data: pricingOptions = []
  } = usePricingOptions();
  const taxNote = useTaxExclusiveNote();
  return <FAQs centeredTitle faqs={[...PRICING_FAQS, pricingTableFaqFAQItem(pricingOptions, currency, taxNote)]} data-sentry-element="FAQs" data-sentry-component="PricingFaqs" data-sentry-source-file="index.page.tsx" />;
};

/**
 * Shared by /pricing and /pricing/business. `businessOnly` is the DEV-6820
 * variant reached from the download modal's "Downloading for a business or
 * client?" link: Pro + Business cards, a Free/Pro/Business comparison table,
 * and no free-plan or custom-licensing banners.
 */
export const PricingPageView = ({
  currency,
  country,
  dehydratedQuery,
  businessOnly = false
}: PricingPageProps & {
  businessOnly?: boolean;
}) => {
  const [upgradeModalPlan, setUpgradeModalPlan] = useAtom(upgradePlanModalPlanAtom);
  useHydrateBillingMode('yearly');
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const openPricingSignupDialog = useOpenPricingSignupDialog();
  const hasHandledSubscribeRecoveryRef = useRef(false);

  // A logged-out visit to /subscribe?plan=... redirects here rather than
  // dead-ending at /login (see pages/subscribe/index.page.tsx). Recover the
  // original intent by opening the sign-up pop-up pre-selected to the same
  // plan, then drop the query params so a refresh/close doesn't reopen it.
  useEffect(() => {
    if (hasHandledSubscribeRecoveryRef.current || isLoggedIn) {
      return;
    }
    const planQuery = typeof router.query.plan === 'string' ? router.query.plan.toLowerCase() : '';
    if (!VALID_PAID_PLAN_NAMES.has(planQuery)) {
      return;
    }
    hasHandledSubscribeRecoveryRef.current = true;
    const periodQuery = typeof router.query.period === 'string' ? router.query.period : '';
    const billingPeriod = isBillingMode(periodQuery) ? periodQuery : 'yearly';
    openPricingSignupDialog(planQuery, billingPeriod);
    void router.replace('/pricing', undefined, {
      shallow: true
    });
  }, [isLoggedIn, router, openPricingSignupDialog]);
  const handleModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setUpgradeModalPlan(null);
    }
  }, [setUpgradeModalPlan]);
  return <HydrationBoundary state={dehydratedQuery} data-sentry-element="HydrationBoundary" data-sentry-component="PricingPageView" data-sentry-source-file="index.page.tsx">
      <PricingCohortProvider initialCountry={country} initialCurrency={currency.code} data-sentry-element="PricingCohortProvider" data-sentry-source-file="index.page.tsx">
        {/* Match the homepage TopNavigation logo position (ml-6, translate-y-7). */}
        <div className="mt-7 ml-6 headerFixed:block hidden w-fit">
          <Link aria-label="Uppbeat homepage" className="w-fit" href="/" data-sentry-element="Link" data-sentry-source-file="index.page.tsx">
            <UBLogo data-sentry-element="UBLogo" data-sentry-source-file="index.page.tsx" />
          </Link>
        </div>
        <div className="">
          <SEO description="Get started on Uppbeat for free with incredible music and no copyright issues. Or unlock everything and unleash your creativity with a Premium or Business plan." title={`Pricing • ${headerTitle} • Uppbeat`} data-sentry-element="SEO" data-sentry-source-file="index.page.tsx">
            {!!businessOnly && <meta content="noindex" name="robots" />}
          </SEO>
          {/* Mounted from the plan list so a plan-change CTA to any paid
              tier (including Business, and downgrades) opens a modal
              instead of silently setting state nothing listens for. */}
          {(['Essentials', 'Creator', 'Pro', 'Business'] as const).map(plan => <UpgradePlanModal key={plan} onOpenChange={handleModalOpenChange} open={upgradeModalPlan === plan} plan={plan} />)}

          <div className="flex flex-col gap-18">
            {/* Header Section */}
            <div className="mt-8 flex flex-col items-center gap-4 px-7 text-center sm:px-container [@media(min-width:600px)]:mt-4">
              {/* Overrides Alpha's text-4xl base only; its `@md:text-5xl`
                  still takes over from 448px up (DEV-6728). */}
              <Alpha className="text-3xl" data-sentry-element="Alpha" data-sentry-source-file="index.page.tsx">
                {businessOnly ? 'Plans for every content need' : 'Simple plans, all you need to create.'}
              </Alpha>
              {/* Copyright Safe Message */}
              <Text className="text-center font-sans text-gray-950 dark:text-white" data-sentry-element="Text" data-sentry-source-file="index.page.tsx">
                {businessOnly ? <>
                    Find the{' '}
                    <span className="font-semibold text-pink-500">
                      right license
                    </span>{' '}
                    to keep your projects{' '}
                    <span className="font-semibold text-pink-500">
                      copyright-protected
                    </span>
                    .
                  </> : <>
                    All plans are{' '}
                    <span className="font-semibold text-pink-500">
                      copyright-safe
                    </span>{' '}
                    for{' '}
                    <span className="font-semibold text-pink-500">
                      worry-free monetization
                    </span>
                    .
                  </>}
              </Text>
            </div>

            {/* Pricing Cards Section */}
            <div className="mx-auto mb-12 w-full max-w-7xl px-7 sm:px-10">
              {businessOnly ? <>
                  <PricingCards className="mx-auto max-w-lg md:max-w-2xl" currency={currency} planTypes={['Pro', 'Business']} />
                  {/* Escape hatch so nobody is locked out of the lower plans. */}
                  <Text className="mt-8 text-center font-sans text-gray-700 text-sm dark:text-gray-400">
                    Using it for personal projects?{' '}
                    <Link className="font-semibold text-pink-500 underline hover:text-pink-600" href="/pricing">
                      See all plans
                    </Link>
                  </Text>
                </> : <>
                  <PricingCards currency={currency} />
                  <div className="mx-auto mt-8 max-w-6xl">
                    {/* The free plan, deemphasized to a banner. Only visitors
                        see it — logged-in users already have an account
                        (DEV-6399/6404/6405). */}
                    {!isLoggedIn && <div className="mb-4">
                        <FreePlanBanner />
                      </div>}
                    {/* Grid rows stretch, so the pair always match in height. */}
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <ApiBanner />
                      <CustomLicensingBanner />
                    </div>
                  </div>
                </>}
            </div>
          </div>
          {/* Payment Assurance Section — dropped on mobile, where it
              stacks into three full-width blocks of small print (DEV-6728). */}
          <div className="mx-auto mb-20 hidden w-full max-w-6xl px-6 sm:block">
            <PaymentAssurance data-sentry-element="PaymentAssurance" data-sentry-source-file="index.page.tsx" />
          </div>
          {/* Safe For All Platforms Section  */}
          <div className="mx-auto mb-20 w-full max-w-6xl px-7 sm:px-6">
            <SafeForAllPlatformsSection data-sentry-element="SafeForAllPlatformsSection" data-sentry-source-file="index.page.tsx" />
          </div>
          {/* Plan Comparison Section */}
          <div className="hidden md:block">
            <SectionTitle data-sentry-element="SectionTitle" data-sentry-source-file="index.page.tsx">Plans & features</SectionTitle>
            <PricingTable currency={currency} planTypes={businessOnly ? ['Free', 'Pro', 'Business'] : undefined} data-sentry-element="PricingTable" data-sentry-source-file="index.page.tsx" />
            <div className="mx-auto mt-8 mb-20 max-w-6xl @max-6xl:px-10">
              <CustomPackageBanner data-sentry-element="CustomPackageBanner" data-sentry-source-file="index.page.tsx" />
            </div>
            {/* Reviews Section */}
            <div className="mx-auto mb-20 w-full">
              <ReviewsSection data-sentry-element="ReviewsSection" data-sentry-source-file="index.page.tsx" />
            </div>
          </div>
          {/* FAQs Section */}
          <div className="fl-mt-12/32 mx-auto w-full max-w-6xl px-7 sm:px-6">
            <PricingFaqs currency={currency} data-sentry-element="PricingFaqs" data-sentry-source-file="index.page.tsx" />
          </div>

          <Footer distractionFree data-sentry-element="Footer" data-sentry-source-file="index.page.tsx" />
        </div>
      </PricingCohortProvider>
    </HydrationBoundary>;
};
const PricingPage: NextPage<PricingPageProps> = props => <PricingPageView {...props} data-sentry-element="PricingPageView" data-sentry-component="PricingPage" data-sentry-source-file="index.page.tsx" />;
export const getServerSideProps = withServerAuth(async (auth, context) => {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery(pricingQuery);
  const countryHeader = context.req.headers['x-vercel-ip-country'];
  const rawCountry = Array.isArray(countryHeader) ? countryHeader[0] : countryHeader;
  // Mirror middleware.api.ts so the GrowthBook `country` attribute is always
  // either a strict 2-letter ISO code or undefined — never the `XX` placeholder
  // Vercel emits for unknown geo, never lowercase, never a non-ISO string.
  // USD-ROW's targeting is `currency=USD AND country $nin [US, PR, ...]`, which
  // matches any present-but-malformed country value (anything not literally in
  // the list), so leaving raw values in `country` leaks those visitors into the
  // ROW cohort.
  const country = normalizeIsoCountry(rawCountry);
  const currency = resolveCurrencyFromRequest({
    cookieValue: context.req.cookies.currency,
    country
  });

  // Resolve the Phase 2 pricing-test cohort server-side and overwrite the
  // prefetched query data so the SSR HTML reflects cohort prices (no flicker
  // on hydration). Falls back to control on any GrowthBook failure.
  //
  // The (currency, country) tuple is also passed to the client via
  // `PricingCohortProvider` so the first client-side evaluation reads the
  // same pricing-test flag we read here. Without that, the hook would default
  // to USD/empty country before `useSettings` resolves and fire a stray
  // `experiment_viewed` against the wrong flag (DEV-5788).
  //
  // Known limitation: for an authenticated user whose backend currency
  // differs from the cookie/geo currency (e.g. a UK account holder browsing
  // from JP), the SSR-applied cohort can still differ from what the client
  // resolves once `useSettings` returns — and because the override is
  // additive, the SSR-applied prices "stick" until the client re-fetches.
  try {
    const {
      planId
    } = JSON.parse(context.req.cookies.userData ?? '{}') as Partial<UserDataCookie>;
    const {
      result,
      cleanup
    } = await withGrowthBook({
      context,
      path: context.resolvedUrl ?? '/pricing',
      customAttributes: {
        currency: currency.code,
        country: country ?? ''
      }
    }, gb => ({
      cohort: resolveCohortSSR(gb, {
        currency: currency.code,
        country: country ?? '',
        planId
      }).cohort
    }));
    const {
      cohort
    } = result;
    try {
      const prefetched = queryClient.getQueryData<PricingOption[]>(pricingQuery.queryKey ?? []);
      if (prefetched) {
        queryClient.setQueryData(pricingQuery.queryKey ?? [], applyPricingOverridesToApiResponse(prefetched, {
          currency: currency.code,
          country: country ?? '',
          cohort
        }));
      }
    } finally {
      cleanup();
    }
  } catch {
    // Cohort resolution is best-effort; fall through to control on any error.
  }

  // Mirrors the gate in usePricingCards.tsx so the SSR prefetch and the
  // client-side useCurrentPrice subscription stay in sync.
  const isPaidSubscriber = auth.isAuthenticated && PAID_PLAN_ROLES.some(role => auth.hasRole(role));
  if (isPaidSubscriber) {
    try {
      await queryClient.prefetchQuery({
        queryKey: systemKeys.currentPrice(),
        queryFn: async () => {
          const response = await v1Api.get<CurrentPriceResponse>('v2/subscription/account/user-price', {
            headers: {
              'X-Skip-CDN': 'true',
              ...(context.req.headers.cookie ? {
                Cookie: context.req.headers.cookie
              } : {})
            }
          });
          // Strip non-serializable AxiosResponse fields (config, request) that
          // would 500 Next.js prop serialization. Consumers read `.data?.data`.
          return {
            data: response.data
          };
        }
      });
    } catch {
      // Swallow — the client will refetch via useCurrentPrice and the page
      // falls back to default plan pricing in the meantime.
    }
  }

  // Body varies by IP-derived currency; prevent shared-cache reuse.
  context.res.setHeader('Cache-Control', 'private, no-store');
  return {
    props: {
      currency,
      // Passed through to PricingCohortProvider so the client hook evaluates
      // the same pricing-test flag SSR did on first paint. See DEV-5788.
      country: country ?? '',
      dehydratedQuery: dehydrate(queryClient)
    }
  };
});
export default PricingPage;